function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import account from "../../data/account";
import * as api from "./images-api";

/*
 * Commerce image workspace, Phase 2 pieces (PlunkDocs specs/IMAGE_WORKSPACE.md §10.3, §10.4, §15, §16, §22;
 * mockup image-workspace-2026-10-05.html section 5). Mounted by image-workspace.jsx:
 *   ShopifyPushDialog  from the Approve and Export sheet ("Push to Shopify")
 *   ColorwaySheet      from the side panel ("Colorway set")
 *   BrushCanvas        on the canvas when Retouch is in Brush mode (+ renderMaskPng for the upload)
 *   FontsSection       inside the Text panel
 *   LanguageChips      inside the Text panel (base + added languages, Translate)
 *   ShopifyBlock, SimilarStrip, StatsStrip   in the side panel
 *   DupNotice          on the upload path ("Looks like N images you already have")
 * House rules: no credit amounts, no prices, no disclaimers, polling only (4 s), ids as strings, every call fail-soft.
 */

const toast = api.toast;
const idStr = v => v === undefined || v === null ? "" : String(v);
function timeAgo(d) {
  if (!d) return "";
  const t = new Date(d);
  if (isNaN(t)) return "";
  const s = Math.max(0, (Date.now() - t.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  return t.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  });
}
const usd = n => {
  const v = Number(n) || 0;
  return v >= 1000 ? `$${Math.round(v).toLocaleString()}` : `$${v.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}`;
};
const num = n => (Number(n) || 0).toLocaleString();
export const stop = e => e.stopPropagation();

/** The value on a version's chain that names it (a recolor target, a create shot) for the SEO filename and the variant default. */
export function chainFacts(family, version) {
  const out = {
    variantValue: "",
    shot: ""
  };
  let p = version;
  const seen = new Set();
  while (p && !seen.has(p.id)) {
    seen.add(p.id);
    const op = p.op || {};
    const a = String(op.action || "").toLowerCase();
    const prm = op.params || {};
    if (!out.variantValue && a === "recolor" && prm.target) out.variantValue = String(prm.target);
    if (!out.shot && prm.shot) out.shot = String(prm.shot);
    if (!out.shot && a === "create" && prm.shotType) out.shot = String(prm.shotType);
    p = p.parentVersionId ? (family.versions || []).find(x => x.id === p.parentVersionId) : null;
  }
  return out;
}
function variantLabel(v) {
  if (!v) return "";
  const opts = (v.options || []).map(o => o && o.value).filter(Boolean);
  return opts.length ? opts.join(" / ") : v.title || v.gid;
}
function fidelityChip(f) {
  if (!f) return /*#__PURE__*/React.createElement("span", {
    className: "plk-chip"
  }, "Unverified");
  const st = String((typeof f === "string" ? f : f.state || f.verdict) || "").toLowerCase();
  const note = typeof f === "object" ? f.note || (f.notes || []).join("; ") : "";
  if (st === "checked" || st === "pass") return /*#__PURE__*/React.createElement("span", {
    className: "plk-chip ok",
    title: note
  }, "Checked");
  if (st === "failed" || st === "fail" || st === "warn") return /*#__PURE__*/React.createElement("span", {
    className: "plk-chip warn",
    title: note
  }, "Needs a look");
  return /*#__PURE__*/React.createElement("span", {
    className: "plk-chip",
    title: note
  }, "Unverified");
}

/* =================================================================== Push to Shopify (mockup section 5) */
export class ShopifyPushDialog extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      data: undefined,
      busy: "",
      err: "",
      variantGid: "",
      mode: "append",
      position: 1,
      replaceMediaGid: "",
      alt: "",
      filename: "",
      touched: false,
      credits: null
    });
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "onKey", e => {
      if (e.key === "Escape" && !this.state.busy) this.props.onClose();
    });
    _defineProperty(this, "load", async () => {
      const {
        family,
        version
      } = this.props;
      let data = null;
      try {
        data = await api.getShopify(family.id);
      } catch (e) {
        if (this._alive) this.setState({
          data: null,
          err: e && e.message || "Could not read the Shopify link."
        });
        return;
      }
      if (!this._alive) return;
      const facts = chainFacts(family, version);
      const product = data && data.product;
      const variants = product && product.variants || [];
      const match = facts.variantValue ? variants.find(v => (v.options || []).some(o => String(o.value || "").toLowerCase() === facts.variantValue.toLowerCase())) : null;
      const variantGid = (match || variants[0] || {}).gid || "";
      const media = product && product.media || [];
      this.setState({
        data,
        variantGid,
        position: media.length + 1,
        alt: (version.altText || "").slice(0, 512),
        filename: this.suggest(data, variantGid, facts)
      });
    });
    _defineProperty(this, "pickVariant", variantGid => {
      const facts = chainFacts(this.props.family, this.props.version);
      this.setState({
        variantGid,
        filename: this.state.touched ? this.state.filename : this.suggest(this.state.data, variantGid, facts)
      });
    });
    _defineProperty(this, "push", async () => {
      const {
        family,
        version
      } = this.props;
      const {
        data,
        variantGid,
        mode,
        position,
        replaceMediaGid,
        alt,
        filename
      } = this.state;
      const product = data && data.product;
      const body = {
        versionId: version.id,
        productId: product ? product.legacyId || product.gid : undefined,
        variantGid: variantGid || undefined,
        alt: alt.trim().slice(0, 512),
        filename: api.cleanFilename(filename)
      };
      if (mode === "position") body.position = Math.max(1, Number(position) || 1);
      if (mode === "replace") {
        body.replaceMediaGid = replaceMediaGid;
        const m = (product.media || []).find(x => x.gid === replaceMediaGid);
        if (m) body.position = m.position;
      }
      this.setState({
        busy: "push",
        err: "",
        credits: null
      });
      try {
        let r;
        try {
          r = await api.pushShopify(family.id, body);
        } catch (e) {
          const code = e && e.data && (e.data.code || e.data.error);
          if (e && e.status === 409 && code === "hires_required") {
            this.setState({
              busy: "hires"
            });
            const h = await api.makeHires(family.id, version.id);
            const jobId = h && (h.jobId || h.id);
            if (jobId) {
              const done = await api.waitJob(jobId, () => this._alive);
              if (done.status === "failed") throw new Error(done.error || "The hi-res render failed.");
              if (done.status === "timeout") {
                this.setState({
                  err: "The hi-res render is still running. Push again when it is done."
                });
                return;
              }
            }
            this.setState({
              busy: "push"
            });
            r = await api.pushShopify(family.id, body);
          } else throw e;
        }
        if (!this._alive) return;
        toast(`v${version.version} pushed to Shopify${r && r.position ? ` at position ${r.position}` : ""}.`);
        this.props.onPushed(r || {});
      } catch (e) {
        if (!this._alive) return;
        const code = e && e.data && (e.data.code || e.data.error);
        if (e && (e.status === 402 || code === "insufficient_credits" || code === "out_of_credits")) this.setState({
          credits: {
            insufficient: true,
            buyUrl: e.data && (e.data.purchasePath || e.data.buyUrl) || "https://account.plunk.io/?from=app"
          }
        });else if (code === "approval_required") this.setState({
          err: "This workspace requires approval before a push. Approve the version first."
        });else if (code === "no_connector") this.setState({
          data: {
            ...(this.state.data || {}),
            connected: false
          }
        });else if (code === "version_locked") this.setState({
          err: "A hi-res render is in flight for this version. Try again in a moment."
        });else if (api.isSoft(e)) this.setState({
          err: "Push to Shopify is not available yet."
        });else this.setState({
          err: e && e.message || "Could not push to Shopify."
        });
      } finally {
        if (this._alive) this.setState({
          busy: ""
        });
      }
    });
  }
  componentDidMount() {
    document.addEventListener("keydown", this.onKey);
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
    document.removeEventListener("keydown", this.onKey);
  }
  brand(data) {
    const shop = String(data && data.shop || "").replace(/^https?:\/\//, "").split(".")[0];
    const a = account.get() || {};
    return shop || a.company || a.account || "";
  }
  suggest(data, variantGid, facts) {
    const {
      family,
      version
    } = this.props;
    const product = data && data.product;
    const v = (product && product.variants || []).find(x => x.gid === variantGid);
    const value = facts.variantValue || (v && (v.options || []).length ? v.options[0].value : "");
    const handle = product && product.handle || family.product && family.product.title || family.root && family.root.title || "";
    return api.seoFilename([this.brand(data), handle, value, facts.shot || (version.aiGenerated ? "studio" : "hero")]);
  }
  render() {
    const {
      family,
      version,
      onClose,
      history
    } = this.props;
    const {
      data,
      busy,
      err,
      variantGid,
      mode,
      position,
      replaceMediaGid,
      alt,
      filename,
      credits
    } = this.state;
    const product = data && data.product;
    const variants = product && product.variants || [];
    const media = (product && product.media || []).slice().sort((a, b) => a.position - b.position);
    const preview = api.cleanFilename(filename);
    const asciiOk = /^[a-z0-9-]+\.jpg$/.test(preview) && preview.length <= 84;
    const canPush = !!data && data.connected && !!product && !busy && alt.trim().length > 0 && asciiOk && (mode !== "replace" || !!replaceMediaGid);
    const approvedBy = version.approvedBy ? ` · approved by ${String(version.approvedBy).split("@")[0]}${version.approvedDate ? ` · ${timeAgo(version.approvedDate)}` : ""}` : "";
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-modal-backdrop hub-edit-backdrop iw-over",
      onClick: () => {
        if (!busy) onClose();
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "iw-sheet ec-form iw-shop",
      onClick: stop
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-tplp-head"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h3", {
      className: "hub-h3",
      style: {
        margin: 0
      }
    }, "Push to Shopify \xB7 v", version.version), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, family.root && family.root.title || family.product && family.product.title || "Image", approvedBy)), /*#__PURE__*/React.createElement("span", {
      className: "hub-tplp-acts"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle sm",
      disabled: !!busy,
      onClick: onClose
    }, "Close"))), data === undefined ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "Reading the connected store\u2026") : data === null ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, err || "The Shopify round trip is not available yet on this environment.") : !data.connected ? /*#__PURE__*/React.createElement("p", {
      className: "iw-shop-off"
    }, "No Shopify store is connected to this workspace. ", /*#__PURE__*/React.createElement("a", {
      href: "#",
      onClick: e => {
        e.preventDefault();
        onClose();
        if (history) history.push("/Shopify");else window.location.hash = "#/Shopify";
      }
    }, "Connect your store"), " and the push goes to the right product and variant.") : !product ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "This image is not linked to a Shopify product. Anchor it to a product from the catalog first; pushes go to that product.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "iw-grid-2"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Product"), /*#__PURE__*/React.createElement("input", {
      className: "ec-input",
      value: `${product.title || "Product"} · shopify:${product.legacyId || product.gid}`,
      disabled: true
    })), /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Variant ", /*#__PURE__*/React.createElement("i", null, "media attaches here")), /*#__PURE__*/React.createElement("select", {
      value: variantGid,
      disabled: !!busy,
      onChange: e => this.pickVariant(e.target.value)
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "Product level (no variant)"), variants.map(v => /*#__PURE__*/React.createElement("option", {
      key: v.gid,
      value: v.gid
    }, variantLabel(v)))))), /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Position"), /*#__PURE__*/React.createElement("div", {
      className: "iw-radio-cards"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "iw-radio-card" + (mode === "append" ? " on" : ""),
      disabled: !!busy,
      onClick: () => this.setState({
        mode: "append"
      })
    }, /*#__PURE__*/React.createElement("b", null, "Add last"), /*#__PURE__*/React.createElement("span", null, "position ", media.length + 1, ", keeps the existing media")), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "iw-radio-card" + (mode === "position" ? " on" : ""),
      disabled: !!busy,
      onClick: () => this.setState({
        mode: "position"
      })
    }, /*#__PURE__*/React.createElement("b", null, "Add as position ", mode === "position" ? position : ""), /*#__PURE__*/React.createElement("span", null, "pick the number below; the rest shift down")), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "iw-radio-card" + (mode === "replace" ? " on" : ""),
      disabled: !!busy || !media.length,
      onClick: () => this.setState({
        mode: "replace",
        replaceMediaGid: replaceMediaGid || (media[0] || {}).gid || ""
      })
    }, /*#__PURE__*/React.createElement("b", null, "Replace existing media"), /*#__PURE__*/React.createElement("span", null, media.length ? "pick which one below" : "no media on this product yet"))), mode === "position" ? /*#__PURE__*/React.createElement("div", {
      className: "iw-pos"
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, "Position"), /*#__PURE__*/React.createElement("input", {
      className: "ec-input ec-num",
      type: "number",
      min: 1,
      max: media.length + 1,
      value: position,
      disabled: !!busy,
      onChange: e => this.setState({
        position: Math.max(1, Math.min(media.length + 1, Number(e.target.value) || 1))
      })
    }), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, "of ", media.length + 1)) : null, mode === "replace" && media.length ? /*#__PURE__*/React.createElement("div", {
      className: "iw-media"
    }, media.map(m => {
      const vs = variants.filter(v => (m.variantGids || []).indexOf(v.gid) !== -1).map(variantLabel);
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: m.gid,
        className: "iw-media-th" + (replaceMediaGid === m.gid ? " on" : ""),
        disabled: !!busy,
        title: m.alt || "",
        onClick: () => this.setState({
          replaceMediaGid: m.gid
        })
      }, /*#__PURE__*/React.createElement("span", {
        className: "im"
      }, m.url ? /*#__PURE__*/React.createElement("img", {
        src: m.url,
        alt: "",
        loading: "lazy"
      }) : /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "image")), /*#__PURE__*/React.createElement("span", {
        className: "cap"
      }, "position ", m.position, vs.length ? ` · ${vs.join(", ")}` : ""));
    })) : null), /*#__PURE__*/React.createElement("div", {
      className: "iw-grid-2"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Alt text ", /*#__PURE__*/React.createElement("i", null, "accessibility \xB7 ", alt.length, "/512")), /*#__PURE__*/React.createElement("textarea", {
      rows: 3,
      value: alt,
      maxLength: 512,
      disabled: !!busy,
      placeholder: "What the image shows, for screen readers and search",
      onChange: e => this.setState({
        alt: e.target.value
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "SEO filename"), /*#__PURE__*/React.createElement("input", {
      className: "ec-input",
      value: filename,
      maxLength: 84,
      disabled: !!busy,
      onChange: e => this.setState({
        filename: e.target.value,
        touched: true
      })
    }), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "Shopify keeps the file name from the URL. Lowercase, letters, digits and dashes, 80 characters max.", /*#__PURE__*/React.createElement("br", null), "Preview: ", /*#__PURE__*/React.createElement("code", null, preview), !asciiOk ? /*#__PURE__*/React.createElement("span", {
      className: "ec-err"
    }, " \xB7 letters, digits and dashes only") : null))), /*#__PURE__*/React.createElement("div", {
      className: "iw-shop-prev"
    }, /*#__PURE__*/React.createElement("span", {
      className: "iw-mini lg",
      style: {
        aspectRatio: version.width && version.height ? `${version.width} / ${version.height}` : "1 / 1"
      }
    }, /*#__PURE__*/React.createElement("img", {
      src: version.url,
      alt: ""
    })), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, version.hasHires ? `Hi-res exists for v${version.version}.` : "The full-resolution file is made first, then pushed.", " Shoppers see the alt text only; the provenance stays with you.")), credits && credits.insufficient ? /*#__PURE__*/React.createElement("div", {
      className: "iw-notice inset"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "info"), /*#__PURE__*/React.createElement("span", null, "Not enough credits for the hi-res render."), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      size: "small",
      onClick: () => window.open(credits.buyUrl, "_blank", "noopener")
    }, "Buy credits")) : null, err ? /*#__PURE__*/React.createElement("div", {
      className: "ec-err",
      style: {
        marginTop: 8
      }
    }, err) : null, /*#__PURE__*/React.createElement("div", {
      className: "iw-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, "Writes to ", data.shop || "your store", " through the connected store."), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !canPush,
      onClick: this.push
    }, busy === "hires" ? "Rendering hi-res…" : busy === "push" ? "Pushing…" : "Push to Shopify"))), data && data.connected && !product && err ? /*#__PURE__*/React.createElement("div", {
      className: "ec-err"
    }, err) : null));
  }
}

/** The side-panel "Shopify" block: the pulled and pushed media rows for this family. */
export function ShopifyBlock({
  shopify,
  family
}) {
  if (!shopify || !shopify.connected) return null;
  const product = shopify.product;
  const rows = shopify.rows || [];
  const vname = gid => {
    const v = (product && product.variants || []).find(x => x.gid === gid);
    return v ? variantLabel(v) : "";
  };
  const vnum = assetId => {
    const v = (family.versions || []).find(x => x.id === assetId);
    return v ? `v${v.version}` : "";
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "iw-card"
  }, /*#__PURE__*/React.createElement("h6", null, "Shopify"), product ? /*#__PURE__*/React.createElement("div", {
    className: "iw-shop-prod"
  }, product.url ? /*#__PURE__*/React.createElement("a", {
    href: product.url,
    target: "_blank",
    rel: "noreferrer"
  }, product.title || "Product") : /*#__PURE__*/React.createElement("span", null, product.title || "Product"), /*#__PURE__*/React.createElement("span", {
    className: "territory-hint"
  }, " \xB7 ", shopify.shop)) : null, !rows.length ? /*#__PURE__*/React.createElement("p", {
    className: "territory-hint",
    style: {
      margin: "4px 0 0"
    }
  }, "No Shopify media linked to this image yet.") : /*#__PURE__*/React.createElement("ul", {
    className: "iw-shop-rows"
  }, rows.map((r, i) => /*#__PURE__*/React.createElement("li", {
    key: r.mediaGid || i
  }, /*#__PURE__*/React.createElement("span", {
    className: "plk-chip" + (r.direction === "push" ? " info" : "")
  }, r.direction === "push" ? "Pushed" : "Pulled"), /*#__PURE__*/React.createElement("span", null, r.position != null ? `position ${r.position}` : "no position", r.variantGid ? ` · ${vname(r.variantGid) || "variant"}` : "", vnum(r.assetId) ? ` · ${vnum(r.assetId)}` : ""), /*#__PURE__*/React.createElement("span", {
    className: "plk-chip " + (r.status === "removed" || r.status === "failed" ? "warn" : r.status === "synced" || r.status === "live" || r.status === "ok" ? "ok" : ""),
    title: r.error || ""
  }, r.status || "linked"), r.syncedDate ? /*#__PURE__*/React.createElement("span", {
    className: "territory-hint"
  }, timeAgo(r.syncedDate)) : null))));
}

/* =================================================================== Colorway set */
export class ColorwaySheet extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      attr: "",
      values: [],
      free: "",
      picks: [],
      batch: null,
      busy: "",
      err: "",
      rejected: {},
      credits: null,
      exported: null
    });
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "_poll", null);
    _defineProperty(this, "onKey", e => {
      if (e.key === "Escape" && !this.state.busy) this.props.onClose();
    });
    _defineProperty(this, "start", async () => {
      const {
        family,
        version
      } = this.props;
      const values = this.valueList();
      if (!values.length) return;
      this.setState({
        busy: "start",
        err: "",
        credits: null
      });
      try {
        const r = await api.startColorways(family.id, {
          versionId: version.id,
          attribute: this.state.attr || "Color",
          values,
          placements: this.state.picks.length ? this.state.picks : undefined
        });
        const batchId = r && (r.batchId || r.id);
        if (!batchId) throw new Error("The set did not start.");
        const jobs = (r && r.jobs || values.map(v => ({
          value: v
        }))).map(j => ({
          jobId: idStr(j.jobId || j.id),
          value: j.value,
          status: "queued",
          outputVersionId: null
        }));
        this.setState({
          batch: {
            batchId: idStr(batchId),
            done: false,
            jobs
          },
          busy: "run"
        });
        this.poll();
      } catch (e) {
        if (!this._alive) return;
        const code = e && e.data && (e.data.code || e.data.error);
        if (e && (e.status === 402 || code === "insufficient_credits" || code === "out_of_credits")) this.setState({
          credits: {
            insufficient: true,
            buyUrl: e.data && (e.data.purchasePath || e.data.buyUrl) || "https://account.plunk.io/?from=app"
          },
          busy: ""
        });else if (api.isSoft(e)) this.setState({
          err: "Colorway sets are not available yet.",
          busy: ""
        });else if (code === "not_a_variant") this.setState({
          err: "Values must come from the product's own options.",
          busy: ""
        });else this.setState({
          err: e && e.message || "Could not start the set.",
          busy: ""
        });
      }
    });
    _defineProperty(this, "poll", () => {
      clearTimeout(this._poll);
      this._poll = setTimeout(async () => {
        if (!this._alive || !this.state.batch) return;
        let b = null;
        try {
          b = await api.getBatch(this.props.family.id, this.state.batch.batchId);
        } catch (e) {
          if (!this._alive) return;
          if (api.isSoft(e)) {
            this.setState({
              err: "Batch polling is not available yet: the versions land in the history rail when they finish.",
              busy: ""
            });
            return;
          }
        }
        if (!this._alive) return;
        if (b) {
          const prev = this.state.batch;
          const jobs = b.jobs.length ? b.jobs : prev.jobs;
          const newlyDone = jobs.some(j => j.outputVersionId && !(prev.jobs.find(p => p.jobId === j.jobId) || {}).outputVersionId);
          this.setState({
            batch: {
              ...b,
              jobs
            }
          });
          if (newlyDone || b.done) await this.props.onReload();
          if (b.done) {
            this.setState({
              busy: ""
            });
            toast(`Colorway set done: ${jobs.filter(j => j.outputVersionId).length} of ${jobs.length} made.`);
            return;
          }
        }
        this.poll();
      }, api.POLL_MS);
    });
    _defineProperty(this, "reject", async job => {
      if (!job.outputVersionId) return;
      this.setState({
        busy: "reject"
      });
      try {
        await api.deleteVersion(job.outputVersionId);
        this.setState({
          rejected: {
            ...this.state.rejected,
            [job.jobId]: true
          }
        });
        toast(`${job.value} rejected (restore from Trash within 30 days).`);
        await this.props.onReload();
      } catch (e) {
        this.setState({
          err: e && e.message || "Could not reject this output."
        });
      } finally {
        if (this._alive) this.setState({
          busy: ""
        });
      }
    });
    _defineProperty(this, "approveSet", async () => {
      const {
        batch,
        overrideNote,
        needNote
      } = this.state;
      if (!batch) return;
      if (needNote && !String(overrideNote || "").trim()) {
        this.setState({
          err: "Add a short note to approve past the failed check."
        });
        return;
      }
      this.setState({
        busy: "approve",
        err: ""
      });
      try {
        await api.approveBatch(this.props.family.id, batch.batchId, "approved", needNote ? overrideNote.trim() : undefined, !!needNote);
        toast("Set approved.");
        this.setState({
          needNote: false,
          overrideNote: ""
        });
        await this.props.onReload();
      } catch (e) {
        if (e && e.status === 403) this.setState({
          err: "Only an administrator or owner can approve."
        });else if (e && e.data && e.data.code === "fidelity_failed") this.setState({
          needNote: true,
          err: "A fidelity check failed in this set. Reject that output, or add a note and approve anyway."
        });else if (api.isSoft(e)) this.setState({
          err: "Set approval is not available yet."
        });else this.setState({
          err: e && e.message || "Could not approve the set."
        });
      } finally {
        if (this._alive) this.setState({
          busy: ""
        });
      }
    });
    _defineProperty(this, "exportSet", async () => {
      const {
        batch,
        picks,
        rejected
      } = this.state;
      const {
        family,
        lang
      } = this.props;
      if (!batch) return;
      const outs = batch.jobs.filter(j => j.outputVersionId && !rejected[j.jobId]);
      const placements = picks.length ? picks : this.props.version.placements || [];
      if (!placements.length) {
        this.setState({
          err: "Tick at least one placement for the set."
        });
        return;
      }
      this.setState({
        busy: "export",
        err: "",
        exported: null
      });
      let n = 0;
      try {
        for (const j of outs) {
          if (!this._alive) return;
          const r = await api.exportImage(family.id, {
            versionId: j.outputVersionId,
            placements,
            language: lang || "en",
            allowDraft: true
          });
          const jobId = r && (r.jobId || r.batchId);
          const ex = r && r.exports;
          if ((!ex || !ex.length) && jobId) {
            const done = await api.waitJob(jobId, () => this._alive);
            if (done.status === "failed") throw new Error(done.error || `Export of ${j.value} failed.`);
          }
          n++;
          this.setState({
            exported: n
          });
        }
        toast(`Exported ${n} colorway${n === 1 ? "" : "s"} × ${placements.length} placement${placements.length === 1 ? "" : "s"}.`);
        await this.props.onReload();
      } catch (e) {
        if (!this._alive) return;
        const code = e && e.data && (e.data.code || e.data.error);
        if (e && (e.status === 402 || code === "insufficient_credits")) this.setState({
          credits: {
            insufficient: true,
            buyUrl: e.data && (e.data.purchasePath || e.data.buyUrl) || "https://account.plunk.io/?from=app"
          }
        });else if (code === "approval_required") this.setState({
          err: "This workspace requires approval before export: approve the set first."
        });else this.setState({
          err: e && e.message || "Could not export the set."
        });
      } finally {
        if (this._alive) this.setState({
          busy: ""
        });
      }
    });
  }
  componentDidMount() {
    document.addEventListener("keydown", this.onKey);
    const keys = Object.keys(this.props.options || {});
    const init = this.props.initial || null; // from the Colors tool: the option and the values already picked there
    const attr = (init && init.attr && keys.indexOf(init.attr) !== -1 ? init.attr : null) || keys.find(k => /colou?r/i.test(k)) || keys[0] || "Color";
    const opts = ((this.props.options || {})[attr] || []).map(String);
    const picked = init && init.values && init.values.length ? init.values.map(String) : null;
    this.setState({
      attr,
      values: picked && opts.length ? picked.filter(v => opts.indexOf(v) !== -1) : opts,
      free: picked && !opts.length ? picked.join(", ") : "",
      picks: (this.props.version.placements || []).slice()
    });
  }
  componentWillUnmount() {
    this._alive = false;
    clearTimeout(this._poll);
    document.removeEventListener("keydown", this.onKey);
  }
  valueList() {
    const {
      attr,
      values,
      free
    } = this.state;
    const opts = (this.props.options || {})[attr] || [];
    return opts.length ? values : free.split(/[,\n]/).map(v => v.trim()).filter(Boolean);
  }
  render() {
    const {
      family,
      version,
      options,
      placements,
      onClose
    } = this.props;
    const {
      attr,
      values,
      free,
      picks,
      batch,
      busy,
      err,
      rejected,
      credits,
      exported
    } = this.state;
    const keys = Object.keys(options || {});
    const optValues = ((options || {})[attr] || []).map(String);
    const list = this.valueList();
    const running = batch && !batch.done;
    const findV = id => (family.versions || []).find(v => v.id === id);
    const made = batch ? batch.jobs.filter(j => j.outputVersionId && !rejected[j.jobId]) : [];
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-modal-backdrop hub-edit-backdrop iw-over",
      onClick: () => {
        if (!busy) onClose();
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "iw-sheet wide ec-form iw-cw",
      onClick: stop
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-tplp-head"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h3", {
      className: "hub-h3",
      style: {
        margin: 0
      }
    }, "This image in other colors"), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "Makes one new version of v", version.version, " for each color you list, all at once. Only the product's color changes; each result is checked against the product, and you approve the whole set together.")), /*#__PURE__*/React.createElement("span", {
      className: "hub-tplp-acts"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle sm",
      disabled: busy === "start",
      onClick: onClose
    }, running ? "Hide" : "Close"))), !batch ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "iw-grid-2"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "What changes ", /*#__PURE__*/React.createElement("i", null, "usually Color, or Fabric / Finish")), keys.length ? /*#__PURE__*/React.createElement("select", {
      value: attr,
      disabled: !!busy,
      onChange: e => this.setState({
        attr: e.target.value,
        values: ((options || {})[e.target.value] || []).map(String)
      })
    }, keys.map(k => /*#__PURE__*/React.createElement("option", {
      key: k,
      value: k
    }, k))) : /*#__PURE__*/React.createElement("input", {
      className: "ec-input",
      value: attr,
      disabled: !!busy,
      placeholder: "Color",
      onChange: e => this.setState({
        attr: e.target.value
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Starting image"), /*#__PURE__*/React.createElement("input", {
      className: "ec-input",
      value: `v${version.version} · ${version.width && version.height ? `${version.width}×${version.height}` : "preview"}`,
      disabled: true
    }))), optValues.length ? /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Colors to make ", /*#__PURE__*/React.createElement("i", null, values.length, " of ", optValues.length, " picked \xB7 from the product's options")), /*#__PURE__*/React.createElement("div", {
      className: "iw-seg"
    }, optValues.map(v => {
      const on = values.indexOf(v) !== -1;
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: v,
        className: "territory-toggle xs" + (on ? " on" : ""),
        disabled: !!busy,
        onClick: () => this.setState({
          values: on ? values.filter(x => x !== v) : [...values, v]
        })
      }, v);
    })), /*#__PURE__*/React.createElement("div", {
      className: "iw-seg",
      style: {
        marginTop: 6
      }
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle xs",
      disabled: !!busy,
      onClick: () => this.setState({
        values: optValues.slice()
      })
    }, "All"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle xs",
      disabled: !!busy,
      onClick: () => this.setState({
        values: []
      })
    }, "None"))) : /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Colors to make ", /*#__PURE__*/React.createElement("i", null, "separate with commas or new lines")), /*#__PURE__*/React.createElement("textarea", {
      rows: 3,
      value: free,
      disabled: !!busy,
      placeholder: "Navy, Olive, Sand",
      onChange: e => this.setState({
        free: e.target.value
      })
    }), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "This product has no color options in the catalog, so typed colors can't be checked against it. The results are marked Unverified until someone approves them.")), /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Also export these sizes ", /*#__PURE__*/React.createElement("i", null, "optional \xB7 same as v", version.version)), /*#__PURE__*/React.createElement("div", {
      className: "iw-seg"
    }, (placements || []).map(p => {
      const on = picks.indexOf(p.key) !== -1;
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: p.key,
        className: "territory-toggle xs" + (on ? " on" : ""),
        disabled: !!busy,
        onClick: () => this.setState({
          picks: on ? picks.filter(x => x !== p.key) : [...picks, p.key]
        })
      }, p.name);
    }))), credits && credits.insufficient ? /*#__PURE__*/React.createElement("div", {
      className: "iw-notice inset"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "info"), /*#__PURE__*/React.createElement("span", null, "Not enough credits for this set."), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      size: "small",
      onClick: () => window.open(credits.buyUrl, "_blank", "noopener")
    }, "Buy credits")) : null, err ? /*#__PURE__*/React.createElement("div", {
      className: "ec-err",
      style: {
        marginTop: 8
      }
    }, err) : null, /*#__PURE__*/React.createElement("div", {
      className: "iw-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, list.length ? `${list.length} version${list.length === 1 ? "" : "s"} from v${version.version}: ${list.slice(0, 6).join(", ")}${list.length > 6 ? "…" : ""}` : "Pick or type at least one color."), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !!busy || !list.length,
      onClick: this.start
    }, busy === "start" ? "Starting…" : `Start ${list.length || ""}`.trim()))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "iw-cw-head"
    }, /*#__PURE__*/React.createElement("span", {
      className: "plk-chip " + (running ? "info" : "ok")
    }, running ? `Working · ${batch.jobs.filter(j => j.outputVersionId || j.status === "done").length} of ${batch.jobs.length}` : `Done · ${made.length} kept`), running ? /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, "polling every 4 s \xB7 the outputs land in the history rail as they finish") : null), /*#__PURE__*/React.createElement("div", {
      className: "iw-cw-grid"
    }, batch.jobs.map(j => {
      const v = j.outputVersionId ? findV(j.outputVersionId) : null;
      const off = !!rejected[j.jobId];
      const failed = j.status === "failed" || j.status === "error";
      return /*#__PURE__*/React.createElement("div", {
        key: j.jobId || j.value,
        className: "iw-cw-card" + (off ? " off" : "")
      }, /*#__PURE__*/React.createElement("div", {
        className: "im",
        style: {
          aspectRatio: version.width && version.height ? `${version.width} / ${version.height}` : "1 / 1"
        }
      }, v && v.url ? /*#__PURE__*/React.createElement("img", {
        src: v.url,
        alt: ""
      }) : failed ? /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "error_outline") : /*#__PURE__*/React.createElement("span", {
        className: "iw-cw-wait"
      }, j.status === "running" ? "rendering…" : "queued")), /*#__PURE__*/React.createElement("div", {
        className: "t"
      }, /*#__PURE__*/React.createElement("b", null, j.value), v ? /*#__PURE__*/React.createElement("span", {
        className: "territory-hint"
      }, " \xB7 v", v.version) : null), /*#__PURE__*/React.createElement("div", {
        className: "ch"
      }, off ? /*#__PURE__*/React.createElement("span", {
        className: "plk-chip"
      }, "Rejected") : failed ? /*#__PURE__*/React.createElement("span", {
        className: "plk-chip warn",
        title: j.error || ""
      }, "Failed") : v ? fidelityChip(j.fidelity || v.fidelity) : null), v && !off ? /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "territory-toggle xs",
        disabled: !!busy,
        onClick: () => this.reject(j)
      }, "Reject") : null, failed && j.error ? /*#__PURE__*/React.createElement("span", {
        className: "ec-err"
      }, String(j.error).slice(0, 80)) : null);
    })), !running ? /*#__PURE__*/React.createElement("div", {
      className: "ec-field",
      style: {
        marginTop: 12
      }
    }, /*#__PURE__*/React.createElement("label", null, "Export placements ", /*#__PURE__*/React.createElement("i", null, picks.length ? `${picks.length} ticked` : "none ticked: uses each version's own ticks")), /*#__PURE__*/React.createElement("div", {
      className: "iw-seg"
    }, (placements || []).map(p => {
      const on = picks.indexOf(p.key) !== -1;
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: p.key,
        className: "territory-toggle xs" + (on ? " on" : ""),
        disabled: !!busy,
        onClick: () => this.setState({
          picks: on ? picks.filter(x => x !== p.key) : [...picks, p.key]
        })
      }, p.name);
    }))) : null, credits && credits.insufficient ? /*#__PURE__*/React.createElement("div", {
      className: "iw-notice inset"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "info"), /*#__PURE__*/React.createElement("span", null, "Not enough credits for this action."), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      size: "small",
      onClick: () => window.open(credits.buyUrl, "_blank", "noopener")
    }, "Buy credits")) : null, err ? /*#__PURE__*/React.createElement("div", {
      className: "ec-err",
      style: {
        marginTop: 8
      }
    }, err) : null, this.state.needNote ? /*#__PURE__*/React.createElement("div", {
      className: "ec-field",
      style: {
        marginTop: 8
      }
    }, /*#__PURE__*/React.createElement("label", null, "Approval note ", /*#__PURE__*/React.createElement("i", null, "why this set is fine despite the failed check")), /*#__PURE__*/React.createElement("input", {
      className: "k-textbox",
      value: this.state.overrideNote || "",
      disabled: !!busy,
      onChange: e => this.setState({
        overrideNote: e.target.value
      }),
      placeholder: "e.g. Finish is intentional for the catalog"
    })) : null, /*#__PURE__*/React.createElement("div", {
      className: "iw-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, exported ? `${exported} exported.` : "Reject trashes that output; Approve set approves every kept output at once."), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement(Button, {
      disabled: !!busy || running || !made.length,
      onClick: this.exportSet
    }, busy === "export" ? `Exporting ${exported || 0} of ${made.length}…` : `Export set (${made.length})`), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !!busy || running || !made.length,
      onClick: this.approveSet
    }, busy === "approve" ? "Approving…" : this.state.needNote ? "Approve anyway" : "Approve set")))));
  }
}

/* =================================================================== Brush masks (Retouch) */
/**
 * A brush canvas over the image at display size. Strokes are kept as fractions of the image box (points + size as a
 * fraction of the width) so they re-render at the version's pixel size for the mask PNG (white = edit, black = keep).
 */
export class BrushCanvas extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      live: null
    });
    _defineProperty(this, "frac", e => {
      const box = this.img.getBoundingClientRect();
      return {
        x: Math.max(0, Math.min(1, (e.clientX - box.left) / box.width)),
        y: Math.max(0, Math.min(1, (e.clientY - box.top) / box.height))
      };
    });
    _defineProperty(this, "down", e => {
      if (!this.props.enabled) return;
      e.preventDefault();
      this.setState({
        live: {
          size: this.props.size,
          pts: [this.frac(e)]
        }
      }, this.draw);
    });
    _defineProperty(this, "move", e => {
      if (!this.state.live) return;
      this.setState({
        live: {
          ...this.state.live,
          pts: [...this.state.live.pts, this.frac(e)]
        }
      }, this.draw);
    });
    _defineProperty(this, "up", () => {
      const l = this.state.live;
      if (!l) return;
      this.setState({
        live: null
      });
      this.props.onStrokes([...(this.props.strokes || []), l]);
    });
    _defineProperty(this, "onLoad", () => {
      if (this.props.onNatural && this.img) this.props.onNatural({
        w: this.img.naturalWidth,
        h: this.img.naturalHeight
      });
      this.draw();
    });
    _defineProperty(this, "draw", () => {
      const c = this.cv,
        img = this.img;
      if (!c || !img) return;
      const w = img.clientWidth,
        h = img.clientHeight;
      if (!w || !h) return;
      if (c.width !== w || c.height !== h) {
        c.width = w;
        c.height = h;
      }
      const ctx = c.getContext("2d");
      ctx.clearRect(0, 0, w, h);
      const all = [...(this.props.strokes || []), ...(this.state.live ? [this.state.live] : [])];
      paintStrokes(ctx, all, w, h, "rgba(255, 99, 88, .55)");
    });
  }
  componentDidMount() {
    window.addEventListener("resize", this.draw);
  }
  componentWillUnmount() {
    window.removeEventListener("resize", this.draw);
  }
  componentDidUpdate() {
    this.draw();
  }
  render() {
    const {
      src,
      enabled,
      size
    } = this.props;
    return /*#__PURE__*/React.createElement("div", {
      className: "iw-brush" + (enabled ? " draw" : ""),
      onMouseDown: this.down,
      onMouseMove: this.move,
      onMouseUp: this.up,
      onMouseLeave: this.up,
      style: {
        "--brush": `${Math.round((size || 0.04) * 100)}`
      }
    }, /*#__PURE__*/React.createElement("img", {
      src: src,
      alt: "",
      draggable: false,
      ref: r => {
        this.img = r;
      },
      onLoad: this.onLoad
    }), /*#__PURE__*/React.createElement("canvas", {
      ref: r => {
        this.cv = r;
      }
    }));
  }
}
function paintStrokes(ctx, strokes, w, h, color) {
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  strokes.forEach(s => {
    const lw = Math.max(1, (s.size || 0.04) * w);
    ctx.lineWidth = lw;
    ctx.beginPath();
    s.pts.forEach((p, i) => {
      if (i === 0) ctx.moveTo(p.x * w, p.y * h);else ctx.lineTo(p.x * w, p.y * h);
    });
    if (s.pts.length === 1) {
      ctx.arc(s.pts[0].x * w, s.pts[0].y * h, lw / 2, 0, Math.PI * 2);
      ctx.fill();
    } else ctx.stroke();
  });
}
/** Render the strokes to a PNG blob at the version's pixel size: white strokes on black. */
export function renderMaskPng(strokes, w, h) {
  return new Promise((resolve, reject) => {
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(w));
    c.height = Math.max(1, Math.round(h));
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, c.width, c.height);
    paintStrokes(ctx, strokes, c.width, c.height, "#fff");
    c.toBlob(b => b ? resolve(b) : reject(new Error("Could not render the mask.")), "image/png");
  });
}
export const BRUSH_SIZES = [[0.02, "Fine"], [0.04, "Medium"], [0.08, "Broad"], [0.14, "Wide"]];

/* =================================================================== Text: languages and fonts */
export function LanguageChips({
  base,
  langs,
  value,
  onPick,
  onAdd,
  onRemove,
  onTranslate,
  busy,
  missing
}) {
  const extra = langs.filter(k => k !== base);
  const avail = api.LANGUAGES.filter(([k]) => langs.indexOf(k) === -1);
  return /*#__PURE__*/React.createElement("div", {
    className: "iw-langs"
  }, /*#__PURE__*/React.createElement("span", {
    className: "iw-seg"
  }, langs.map(k => /*#__PURE__*/React.createElement("span", {
    key: k,
    className: "iw-langchip"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "territory-toggle xs" + (value === k ? " active" : "") + (missing.indexOf(k) !== -1 ? " empty" : ""),
    disabled: busy,
    title: `${api.langName(k)}${k === base ? " (base)" : missing.indexOf(k) !== -1 ? " · no text yet" : ""}`,
    onClick: () => onPick(k)
  }, k.toUpperCase()), k !== base ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "x",
    title: `Remove ${api.langName(k)}`,
    disabled: busy,
    onClick: () => onRemove(k)
  }, "\xD7") : null)), /*#__PURE__*/React.createElement("select", {
    className: "iw-addlang",
    value: "",
    disabled: busy,
    title: "Add language",
    onChange: e => {
      if (e.target.value) onAdd(e.target.value);
    }
  }, /*#__PURE__*/React.createElement("option", {
    value: ""
  }, "Add language"), avail.map(([k, n]) => /*#__PURE__*/React.createElement("option", {
    key: k,
    value: k
  }, n, " (", k, ")"))), extra.length ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "territory-toggle xs",
    disabled: busy,
    title: `Translate the layers from ${api.langName(base)} into ${extra.map(api.langName).join(", ")}`,
    onClick: onTranslate
  }, busy === "translate" ? "Translating…" : "Translate") : null));
}
const LICENSES = [["own", "Our own font"], ["google_ofl", "Google Fonts (OFL)"], ["licensed", "Licensed"], ["unknown", "Unknown"]];
export class FontsSection extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      open: false,
      file: null,
      family: "",
      license: "own",
      note: "",
      busy: "",
      err: "",
      confirm: ""
    });
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "pick", e => {
      const f = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!f) return;
      if (!/\.(ttf|otf|woff2)$/i.test(f.name)) {
        this.setState({
          err: "Pick a .ttf, .otf or .woff2 file."
        });
        return;
      }
      this.setState({
        file: f,
        err: "",
        family: this.state.family || f.name.replace(/\.(ttf|otf|woff2)$/i, "").replace(/[-_]+/g, " ")
      });
    });
    _defineProperty(this, "upload", async () => {
      const {
        file,
        family,
        license,
        note
      } = this.state;
      if (!file || !family.trim()) return;
      this.setState({
        busy: "up",
        err: ""
      });
      try {
        const row = await api.uploadFont(file, {
          family: family.trim(),
          license,
          licenseNote: note.trim(),
          onProgress: p => {
            if (this._alive) this.setState({
              busy: `up:${p}`
            });
          }
        });
        if (!this._alive) return;
        toast(`${family.trim()} added to the fonts registry.`);
        this.setState({
          open: false,
          file: null,
          family: "",
          note: "",
          license: "own"
        });
        this.props.onFontsChange({
          ...(this.props.fonts || {
            system: []
          }),
          items: [...(this.props.fonts && this.props.fonts.items || []), row].filter(Boolean)
        });
        if (row && row.id) this.props.onChange(row.family || family.trim(), row);
      } catch (e) {
        if (this._alive) this.setState({
          err: api.isSoft(e) ? "The fonts registry is not available yet." : e && e.message || "The font upload did not go through."
        });
      } finally {
        if (this._alive) this.setState({
          busy: ""
        });
      }
    });
    _defineProperty(this, "remove", async f => {
      this.setState({
        busy: "del",
        err: "",
        confirm: ""
      });
      try {
        await api.deleteFont(f.id);
        toast(`${f.family} removed.`);
        this.props.onFontsChange({
          ...this.props.fonts,
          items: (this.props.fonts.items || []).filter(x => x.id !== f.id)
        });
        if (this.props.value === f.family) this.props.onChange("brand", null);
      } catch (e) {
        if (this._alive) this.setState({
          err: e && e.message || "Could not remove the font."
        });
      } finally {
        if (this._alive) this.setState({
          busy: ""
        });
      }
    });
  }
  componentWillUnmount() {
    this._alive = false;
  }
  render() {
    const {
      fonts,
      value,
      onChange,
      disabled
    } = this.props;
    const {
      open,
      file,
      family,
      license,
      note,
      busy,
      err,
      confirm
    } = this.state;
    const items = fonts && fonts.items || [];
    const system = fonts && fonts.system && fonts.system.length ? fonts.system : ["Inter", "DejaVu Sans"];
    const picked = items.find(f => f.family === value);
    return /*#__PURE__*/React.createElement("div", {
      className: "iw-fonts"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Font", /*#__PURE__*/React.createElement("span", {
      className: "iw-lang"
    }, fonts !== null ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle xs",
      disabled: disabled || !!busy,
      onClick: () => this.setState({
        open: !open,
        err: ""
      })
    }, open ? "Cancel" : "Upload font") : null)), /*#__PURE__*/React.createElement("select", {
      value: value || "brand",
      disabled: disabled,
      onChange: e => {
        const f = items.find(x => x.family === e.target.value);
        onChange(e.target.value, f || null);
      }
    }, /*#__PURE__*/React.createElement("option", {
      value: "brand"
    }, "Brand kit"), /*#__PURE__*/React.createElement("optgroup", {
      label: "System"
    }, system.map(s => /*#__PURE__*/React.createElement("option", {
      key: s,
      value: s
    }, s))), items.length ? /*#__PURE__*/React.createElement("optgroup", {
      label: "Your fonts"
    }, items.map(f => /*#__PURE__*/React.createElement("option", {
      key: f.id,
      value: f.family
    }, f.family, f.license === "unknown" ? " (no license on file)" : ""))) : null)), picked && picked.license === "unknown" ? /*#__PURE__*/React.createElement("div", {
      className: "ec-help iw-warn"
    }, "No license on file for ", picked.family, ": it is omitted on paid placements until the license is set.") : null, picked ? /*#__PURE__*/React.createElement("div", {
      className: "iw-fontrow"
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, (LICENSES.find(([k]) => k === picked.license) || [])[1] || picked.license, picked.licenseNote ? ` · ${picked.licenseNote}` : ""), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), confirm === picked.id ? /*#__PURE__*/React.createElement("span", {
      className: "iw-confirm"
    }, /*#__PURE__*/React.createElement("span", null, "Remove ", picked.family, "?"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle xs danger",
      disabled: !!busy,
      onClick: () => this.remove(picked)
    }, "Remove"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle xs",
      onClick: () => this.setState({
        confirm: ""
      })
    }, "Keep")) : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle xs",
      disabled: disabled || !!busy,
      onClick: () => this.setState({
        confirm: picked.id
      })
    }, "Remove")) : null, fonts === null ? /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "The fonts registry is not available yet: the brand kit and system fonts render today.") : null, open ? /*#__PURE__*/React.createElement("div", {
      className: "iw-fontform"
    }, /*#__PURE__*/React.createElement("div", {
      className: "iw-grid-2"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "File ", /*#__PURE__*/React.createElement("i", null, ".ttf .otf .woff2")), /*#__PURE__*/React.createElement("input", {
      type: "file",
      accept: ".ttf,.otf,.woff2",
      style: {
        display: "none"
      },
      ref: r => {
        this._file = r;
      },
      onChange: this.pick
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle sm",
      disabled: !!busy,
      onClick: () => this._file && this._file.click()
    }, file ? file.name : "Choose the file")), /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Family"), /*#__PURE__*/React.createElement("input", {
      className: "ec-input",
      value: family,
      disabled: !!busy,
      placeholder: "e.g. Harbor Grotesk",
      onChange: e => this.setState({
        family: e.target.value
      })
    }))), /*#__PURE__*/React.createElement("div", {
      className: "iw-grid-2"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "License"), /*#__PURE__*/React.createElement("select", {
      value: license,
      disabled: !!busy,
      onChange: e => this.setState({
        license: e.target.value
      })
    }, LICENSES.map(([k, l]) => /*#__PURE__*/React.createElement("option", {
      key: k,
      value: k
    }, l)))), /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", null, "Note ", /*#__PURE__*/React.createElement("i", null, "optional")), /*#__PURE__*/React.createElement("input", {
      className: "ec-input",
      value: note,
      disabled: !!busy,
      placeholder: "e.g. desktop + web license, order 1234",
      onChange: e => this.setState({
        note: e.target.value
      })
    }))), license === "unknown" ? /*#__PURE__*/React.createElement("div", {
      className: "ec-help iw-warn"
    }, "Unknown-license fonts render in previews but are omitted on paid placements.") : null, /*#__PURE__*/React.createElement("div", {
      className: "iw-foot",
      style: {
        marginTop: 6,
        paddingTop: 8
      }
    }, err ? /*#__PURE__*/React.createElement("span", {
      className: "ec-err"
    }, err) : /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, "Google Fonts are OFL and fine for rendered images."), /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      size: "small",
      disabled: !!busy || !file || !family.trim(),
      onClick: this.upload
    }, busy && busy.indexOf("up") === 0 ? busy.indexOf(":") !== -1 ? `Uploading · ${busy.split(":")[1]}%` : "Uploading…" : "Add font"))) : err ? /*#__PURE__*/React.createElement("div", {
      className: "ec-err"
    }, err) : null);
  }
}

/* =================================================================== Similar images, per-image stats, near-duplicates */
/** Only convincing matches belong under this heading; the embedding scores unrelated images around 0.5. */
export const SIMILAR_FLOOR = 0.75;
export function SimilarStrip({
  items,
  onOpen
}) {
  items = (items || []).filter(s => s.score == null || Number(s.score) >= SIMILAR_FLOOR);
  if (!items.length) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: "iw-card"
  }, /*#__PURE__*/React.createElement("h6", null, "Similar images ", /*#__PURE__*/React.createElement("span", {
    className: "territory-hint"
  }, "\xB7 same product or a close re-shoot")), /*#__PURE__*/React.createElement("div", {
    className: "iw-sim"
  }, items.map(s => /*#__PURE__*/React.createElement("button", {
    type: "button",
    key: s.id,
    className: "iw-ref",
    title: `${s.title || "image"}${s.score != null ? ` · ${Math.round(s.score * 100)}% match` : ""}`,
    onClick: () => onOpen(s)
  }, /*#__PURE__*/React.createElement("span", {
    className: "im"
  }, s.url ? /*#__PURE__*/React.createElement("img", {
    src: s.url,
    alt: "",
    loading: "lazy"
  }) : /*#__PURE__*/React.createElement("span", {
    className: "material-icons"
  }, "image")), s.score != null ? /*#__PURE__*/React.createElement("span", {
    className: "kind"
  }, Math.round(s.score * 100), "%") : null, /*#__PURE__*/React.createElement("span", {
    className: "cap"
  }, s.title || "untitled")))));
}
export function StatsStrip({
  stats
}) {
  if (!stats) return null;
  const t = stats.totals || {};
  const zero = !(t.sessions || t.conversions || t.conversionValue);
  return /*#__PURE__*/React.createElement("div", {
    className: "iw-card iw-stats"
  }, /*#__PURE__*/React.createElement("h6", null, "Last 30 days"), zero ? /*#__PURE__*/React.createElement("p", {
    className: "territory-hint",
    style: {
      margin: 0
    }
  }, "No traffic credited to this image yet. Links from its exports carry its id, so sessions show up here once it ships.") : /*#__PURE__*/React.createElement("div", {
    className: "iw-stats-row"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, num(t.sessions)), " sessions"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, num(t.conversions)), " conversions"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, usd(t.conversionValue)), " revenue")));
}
export function DupNotice({
  items,
  onKeep,
  onOpen,
  onCancel,
  busy
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: "iw-dup"
  }, /*#__PURE__*/React.createElement("b", null, "Looks like ", items.length, " image", items.length === 1 ? "" : "s", " you already have"), /*#__PURE__*/React.createElement("div", {
    className: "iw-refs"
  }, items.map(s => /*#__PURE__*/React.createElement("button", {
    type: "button",
    key: s.id,
    className: "iw-ref",
    disabled: busy,
    title: s.title || "",
    onClick: () => onOpen(s)
  }, /*#__PURE__*/React.createElement("span", {
    className: "im"
  }, s.url ? /*#__PURE__*/React.createElement("img", {
    src: s.url,
    alt: "",
    loading: "lazy"
  }) : /*#__PURE__*/React.createElement("span", {
    className: "material-icons"
  }, "image")), /*#__PURE__*/React.createElement("span", {
    className: "kind"
  }, s.score != null ? `${Math.round(s.score * 100)}%` : s.distance != null ? `close` : "match"), /*#__PURE__*/React.createElement("span", {
    className: "cap"
  }, s.title || "untitled")))), /*#__PURE__*/React.createElement("div", {
    className: "iw-foot",
    style: {
      marginTop: 10
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "territory-hint"
  }, "Open the existing image to edit from it, or keep the upload as a new family."), /*#__PURE__*/React.createElement("span", {
    className: "sp"
  }), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "territory-toggle sm",
    disabled: busy,
    onClick: onCancel
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    disabled: busy,
    onClick: () => onOpen(items[0])
  }, "Open existing"), /*#__PURE__*/React.createElement(Button, {
    themeColor: "primary",
    size: "small",
    disabled: busy,
    onClick: onKeep
  }, "Keep anyway")));
}